import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import {
  useMutation,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'
import { ICFDISyncForm } from 'models/invoices.models'
import RazonSocial from 'models/razonSocial'
import { STORE_EVENT_TYPES } from 'lib/enums/shared.enums'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import { errorToastOptions, successToastOptions } from 'lib/utils/toast.utils'
import useDialog from 'hooks/Shared/useDialog'
import { useAppSelector } from 'store/hooks'
import { setStoreEvent } from 'store/StoreEvent/storeEvent.reducer'
import { resetSyncValues } from 'actions/invoices'
import { IApiError, IWorkspaceRequest } from 'services/Shared/shared.models'
import {
  syncCFDIs,
  syncCFDIsMultipleWorkspaces,
} from 'services/Shared/shared.services'

interface IUseSyncCFDIsProps {
  emisoresQuery?: UseQueryResult<RazonSocial[], IApiError>
  emisor?: RazonSocial
  selectedWorkspaces?: IWorkspaceRequest[]
  onSuccess?: () => void
}

export interface IUseSyncCFDIs {
  isCFDIsSyncOpen: boolean
  syncMutation: UseMutationResult<void, IApiError, ICFDISyncForm>
  toggleSyncDialog: () => void
}

export const useSyncCFDIs = ({
  emisoresQuery,
  emisor,
  onSuccess,
  selectedWorkspaces,
}: IUseSyncCFDIsProps): IUseSyncCFDIs => {
  const dispatch = useDispatch()

  const dialog = useDialog()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)

  const [isCFDIsSyncOpen, setIsCFDISyncOpen] = useState(false)

  const { icon, closeButton, ...loadingToastOptions } = successToastOptions

  const toggleSyncDialog = () => setIsCFDISyncOpen(!isCFDIsSyncOpen)

  const mutation = useMutation<void, IApiError, ICFDISyncForm>(
    ({ endMonth, startMonth, year }) => {
      if (!year || !startMonth || !endMonth)
        throw new Error(
          'No hay información suficiente para sincronizar los CFDIs'
        )
      if (!emisor && !selectedWorkspaces) {
        throw new Error('emitter')
      }

      if (selectedWorkspaces) {
        return syncCFDIsMultipleWorkspaces({
          activeWorkspace,
          endMonth,
          selectedWorkspaces,
          startMonth,
          year,
        })
      }

      const typedEmisor = emisor as RazonSocial

      return syncCFDIs({
        activeWorkspace,
        emisor: typedEmisor,
        endMonth,
        startMonth,
        year,
      })
    },
    {
      onMutate: () => {
        toast.loading(`Sincronizando CFDIs`, {
          ...loadingToastOptions,
          toastId: 'loading',
        })
      },
      onSuccess: () => {
        dispatch(setStoreEvent(STORE_EVENT_TYPES.CHECK_CFDIS_SYNC_STATUS))
        toast.success(
          'Se ha iniciado la sincronización, le notificaremos cuando termine',
          successToastOptions
        )
        onSuccess && onSuccess()
      },
      onError: error => {
        if (error.message === 'emitter') {
          dialog.danger({
            title: 'generic.error.title',
            subtitle: 'No se ha podido obtener el RFC del emisor.',
            primaryButtonText: 'Actualizar',
            secondaryButtonText: 'Cancelar',
            onPrimaryClick: () => {
              emisoresQuery && emisoresQuery.refetch()
            },
            onClose: () => {
              toggleSyncDialog()
              dispatch(resetSyncValues())
            },
          })

          return
        }

        toast.error(`Hubo un error al sincronizar los CFDIs`, errorToastOptions)
      },
      onSettled: () => {
        toast.dismiss('loading')
        toggleSyncDialog()
        dispatch(resetSyncValues())
      },
    }
  )

  return {
    isCFDIsSyncOpen,
    syncMutation: mutation,
    toggleSyncDialog,
  }
}

export default useSyncCFDIs
